import { deleteJson, getJson, putJson } from "../client";
import { RESUME_BASE } from "./base";

/** A person's protected answers, kept only to fill forms at their direction. */

export type Gender = "MAN" | "WOMAN" | "NON_BINARY" | "DECLINE";
export type Race =
    | "AMERICAN_INDIAN_OR_ALASKA_NATIVE"
    | "ASIAN"
    | "BLACK_OR_AFRICAN_AMERICAN"
    | "NATIVE_HAWAIIAN_OR_PACIFIC_ISLANDER"
    | "WHITE";
export type YesNoDecline = "YES" | "NO" | "DECLINE";
export type Veteran = "PROTECTED_VETERAN" | "NOT_A_PROTECTED_VETERAN" | "DECLINE";
export type SexualOrientation =
    "HETEROSEXUAL" | "GAY_OR_LESBIAN" | "BISEXUAL" | "ANOTHER" | "DECLINE";

/**
 * How soon the person could start: now, or after a notice of 1 to 365
 * days, 52 weeks or 12 months. `resume_api::demographics::NoticePeriod`.
 */
export type NoticePeriod = { unit: "now" } | { unit: "days" | "weeks" | "months"; amount: number };

/**
 * A person's protected answers. Every self-identification answer and
 * `relocation` starts as `DECLINE`; a country list and the notice period
 * are absent until answered, and an empty list is the answer "none".
 * `resume_api::demographics::Demographics`.
 */
export interface Demographics {
    citizenships?: string[] | null;
    authorized_countries?: string[] | null;
    sponsorship_countries?: string[] | null;
    gender: Gender;
    races: Race[];
    hispanic_latino: YesNoDecline;
    veteran: Veteran;
    disability: YesNoDecline;
    sexual_orientation: SexualOrientation;
    transgender: YesNoDecline;
    /** Willing to relocate for a job. */
    relocation: YesNoDecline;
    notice_period?: NoticePeriod | null;
}

/** The statement the person agrees to before any self-identification answer is kept. */
export interface ConsentStatement {
    version: string;
    /** Shown word for word beside the box. */
    text: string;
}

/** The consent on record, while it stands. `resume_api::consent::EeoConsentRecord`. */
export interface EeoConsentRecord {
    statement_version: string;
    given_at: string;
}

/** The box ticked beside the statement of `statement_version`. `resume_api::consent::EeoConsent`. */
export interface EeoConsent {
    agreed: boolean;
    statement_version: string;
}

/** One person's answers as they stand. `resume_api::demographics::DemographicsAnswers`. */
export interface DemographicsAnswers extends Demographics {
    user_id: string;
    /** Absent for a person who never saved. */
    updated_at?: string | null;
    /** The consent to keep self-identification answers, while it stands. */
    consent?: EeoConsentRecord | null;
    /** The statement in force, and the version a save names. */
    consent_statement: ConsentStatement;
}

/** The caller's protected answers, every default until saved. */
export function getDemographics(): Promise<DemographicsAnswers> {
    return getJson(`${RESUME_BASE}/demographics`);
}

/**
 * Saves the answers whole, over any saved before. A save that keeps any
 * self-identification answer other than `DECLINE` carries the person's
 * consent, or is refused with 400; one that keeps none sends none.
 */
export function saveDemographics(
    answers: Demographics,
    consent?: EeoConsent,
): Promise<DemographicsAnswers> {
    return putJson(`${RESUME_BASE}/demographics`, consent ? { ...answers, consent } : answers);
}

/**
 * Withdraws the consent: every self-identification answer goes back to
 * `DECLINE`, and the withdrawal is recorded. The rest stays.
 */
export function withdrawEeoConsent(): Promise<DemographicsAnswers> {
    return deleteJson(`${RESUME_BASE}/demographics/consent`);
}
